import { Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { Cart } from '@spartacus/cart/base/root';
import { createFrom, EventService, StateEventService, WindowRef } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Observable } from 'rxjs';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { CustomCleanEcommerceEvent } from "../../common/custom-common.events";
import { EcommerceType } from "../../tag-manager-feature.model";
import { PLACE_ORDER } from '../checkout-steps/store/actions/custom-checkout.action';
import { CustomOrderPlacedEvent } from './custom-custom-checkout.events';
import { TagManagerFeatureService } from '../../tag-manager-feature.service';

@Injectable({
  providedIn: 'root',
})
export class CustomCheckoutEventBuilder {
  constructor(
    protected actions: ActionsSubject,
    protected stateEventService: StateEventService,
    protected eventService: EventService,
    protected userService: UserAccountFacade,
    protected tagManagerFeatureService: TagManagerFeatureService,
    protected winRef?: WindowRef,
  ) {
    this.register();
  }

  /**
   * Registers checkout events
   */
  protected register(): void {
    this.orderPlacedEvent();
    this.eventService.register(CustomOrderPlacedEvent, this.buildPurchaseEvent());
  }

  /**
   * Builds the purchase events
   */
  protected buildPurchaseEvent(): Observable<CustomOrderPlacedEvent> {
    return this.actions.pipe(
      ofType(PLACE_ORDER),//CheckoutActions.PLACE_ORDER_SUCCESS),
      filter((action) => {
        const storage = this.winRef?.localStorage?.getItem(EcommerceType.PURCHASE); 
        if (!storage) {
          this.winRef?.localStorage?.setItem(EcommerceType.PURCHASE, (<any>action).payload.code);
          return true;
        }
        this.winRef?.localStorage?.setItem(EcommerceType.PURCHASE, storage + `,${(<any>action).payload.code}`);
        return !storage.includes((<any>action).payload.code);
      }),
      withLatestFrom(this.userService.get()),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ ecommerce: null }, CustomCleanEcommerceEvent)),
      map(([action, user]) => {
        const order = (<any>action).payload;
        return createFrom(CustomOrderPlacedEvent, {
          event: EcommerceType.PURCHASE,
          ecommerce: {
            transaction_id: order.code,
            userGroupId: user?.exclusiveRole || '',
            value: order.totalPrice.value.toString(),
            tax: order.totalTax.value.toString(),
            shipping: order.deliveryCost.value.toString(),
            currency: order.totalPrice.currencyIso,
            coupon: this.getOrderPromotionName(order) || '',
            items: this.tagManagerFeatureService.getOrderEntries(order, order.giftCardProduct),
          }
        });
      }),
    );
  }

  private getOrderPromotionName(cart: Cart): string | undefined {
    if (cart && cart.appliedOrderPromotions && cart.appliedOrderPromotions.length) {
      return cart.appliedOrderPromotions[0].promotion.code
    }
    return undefined;
  }

  /**
   * Register an order successfully placed event
   */
  protected orderPlacedEvent(): void {
    this.stateEventService.register({
      action: PLACE_ORDER,
      event: CustomOrderPlacedEvent,
    });
  }


  public dispatchPurchaseModeCompleteEvent(order?: any): Observable<any> {
    return this.userService.get().pipe(
      filter(() => {
        const storage = this.winRef?.localStorage?.getItem(EcommerceType.PURCHASE); 
        if (!storage) {
          this.winRef?.localStorage?.setItem(EcommerceType.PURCHASE, order.code);
          return true;
        }
        if (!storage.includes(order.code)) {
          this.winRef?.localStorage?.setItem(EcommerceType.PURCHASE, storage + `,${order.code}`);
          return true;
        } else {
          return false;
        }
      }),
      tap((user) => {
        this.eventService.dispatch(<CustomCleanEcommerceEvent>{ ecommerce: null }, CustomCleanEcommerceEvent);
        this.eventService.dispatch(<CustomOrderPlacedEvent>{
          event: EcommerceType.PURCHASE,
          ecommerce: {
            transaction_id: order.code,
            userGroupId: user?.exclusiveRole ?? '',
            value: order.totalPrice.value.toString(),
            tax: order.totalTax.value.toString(),
            shipping: order.deliveryCost.value.toString(),
            currency: order.totalPrice.currencyIso,
            coupon: this.getOrderPromotionName(order) || '',
            items: this.tagManagerFeatureService.getOrderEntries(order, order.giftCardProduct),
          }
        }, CustomOrderPlacedEvent);
      })
    )
  }
}
